/**
 * Phase 6 — Drone classes.
 *
 * Pre-P6 every drone was a Freighter (one speed, one capacity, solids only).
 * The lore calls for six classes specialising along three axes: which phases
 * of cargo they can carry (solid / fluid / gas / cryo), how fast and how big,
 * and a per-class price multiplier on the existing base+growth curve.
 *
 *   Class         Phases                  Cap   Speed   Cost×   Vibe
 *   freighter     solid                    5    0.60    1.00×   Workhorse: the only one that existed pre-P6.
 *   tanker        fluid                    8    0.50    1.20×   Slower but bigger; doesn't lose cargo over time.
 *   cryo          fluid (cryogenic)        6    0.45    1.60×   Insulated: carries cryogenic fluids; bleeds
 *                                                              `boilOffPerTick` units per tick in transit.
 *   hauler        solid                   12    0.40    1.50×   Bulk freight — slow, huge.
 *   interceptor   solid, fluid             3    1.00    1.80×   Fast & flexible; tiny cargo.
 *   runner        gas                      4    0.70    1.30×   Pressure-rated: the only class that moves gases.
 *
 * `cargoPhases` is the only hard gate the sim enforces on rules. The
 * `cryogenic` flag on a resource (added in P6) routes carriage to the cryo
 * class even though the resource is "fluid" — `acceptsResource` consults
 * both fields together.
 */
export const DRONE_SPECS = {
    freighter: {
        id: 'freighter',
        name: 'Freighter',
        label: 'F',
        capacity: 5,
        speed: 0.6,
        costMultiplier: 1,
        cargoPhases: ['solid'],
        carriesCryogenic: false,
        requiresResearch: 'unlock-drone-freighter',
    },
    tanker: {
        id: 'tanker',
        name: 'Tanker',
        label: 'T',
        capacity: 8,
        speed: 0.5,
        costMultiplier: 1.2,
        cargoPhases: ['fluid'],
        // Tankers are insulated but not refrigerated — they happily carry fuel,
        // not LH2. The cryo class exists because of the boil-off deduction.
        carriesCryogenic: false,
        requiresResearch: 'unlock-drone-tanker',
    },
    cryo: {
        id: 'cryo',
        name: 'Cryo Hauler',
        label: 'C',
        capacity: 6,
        speed: 0.45,
        costMultiplier: 1.6,
        cargoPhases: ['fluid'],
        boilOffPerTick: 0.05,
        carriesCryogenic: true,
        requiresResearch: 'unlock-drone-cryo',
    },
    hauler: {
        id: 'hauler',
        name: 'Bulk Hauler',
        label: 'B',
        capacity: 12,
        speed: 0.4,
        costMultiplier: 1.5,
        cargoPhases: ['solid'],
        carriesCryogenic: false,
        requiresResearch: 'unlock-drone-hauler',
    },
    interceptor: {
        id: 'interceptor',
        name: 'Interceptor',
        label: 'I',
        capacity: 3,
        speed: 1,
        costMultiplier: 1.8,
        // Interceptors take both phases — they're the priority-runner of the
        // fleet, so a player can use them to plug emergency holes anywhere.
        cargoPhases: ['solid', 'fluid'],
        carriesCryogenic: false,
        requiresResearch: 'unlock-drone-interceptor',
    },
    runner: {
        id: 'runner',
        name: 'Pressure Runner',
        label: 'R',
        capacity: 4,
        speed: 0.7,
        costMultiplier: 1.3,
        cargoPhases: ['gas'],
        carriesCryogenic: false,
        requiresResearch: 'unlock-drone-runner',
    },
};
export const ALL_DRONE_CLASSES = Object.keys(DRONE_SPECS);
export function getDroneSpec(id) {
    const s = DRONE_SPECS[id];
    if (!s)
        throw new Error(`Unknown drone class: ${id}`);
    return s;
}
/**
 * True iff a drone of the given class is rated to carry the given resource.
 * Combines two checks — phase compatibility AND the cryogenic flag — so the
 * server has a single helper to validate `placeDrone` and `configureDroneRule`.
 */
export function droneClassAccepts(klass, resource) {
    const spec = DRONE_SPECS[klass];
    if (!spec.cargoPhases.includes(resource.phase))
        return false;
    if (resource.cryogenic && !spec.carriesCryogenic)
        return false;
    return true;
}
/**
 * Returns the cheapest valid class for a resource — used by the commission
 * panel as a first-cut suggestion. Solids → freighter, fluids → tanker (or
 * cryo), gases → runner.
 */
export function pickDefaultClassFor(resource) {
    if (resource.cryogenic)
        return 'cryo';
    if (resource.phase === 'gas')
        return 'runner';
    if (resource.phase === 'fluid')
        return 'tanker';
    return 'freighter';
}
/** Helper: list every class that can carry `resource`. UI filter for pickers. */
export function classesAcceptingResource(resourceId, byId) {
    const r = byId[resourceId];
    if (!r)
        return [];
    return ALL_DRONE_CLASSES.filter((k) => droneClassAccepts(k, r));
}
//# sourceMappingURL=drones.js.map